import './DocumentTemplateRenameModal.scss';
import React, { useState } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import Rodal from '../../../../components/Rodal/Rodal';
import * as yup from 'yup';
import { Form, Field } from '@availity/form';
import createNotification from '../../../../utils/createNotification';
import Loader from 'react-loaders';
import { ModalBody, ModalHeader } from 'reactstrap';

export interface RenameModalProps {
    templateId: number;
    currentName: string;
    onClose: () => void;
    fetchData: () => void;
    isVisible: boolean;
    modalTitle?: string;
}

interface CopyTemplateFormValues {
    newName: string;
}

const DocumentTemplateRenameModal = (props: RenameModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [isLoading, setIsLoading] = useState(false);

    const [existingNames, setExistingNames] = useState([props.currentName]);

    const rename = (newName: string) => {
        axios
            .put(`/api/SignTemplates/${props.templateId}/name`, {
                name: newName,
            })
            .then((response: any) => {
                if (response) {
                    if (response.status === 200) {
                        createNotification(
                            'Item renamed successfully',
                            'success'
                        );
                        props.onClose();
                        props.fetchData();
                    } else if (
                        response.response &&
                        response.response.status === 409
                    ) {
                        setExistingNames([...existingNames, newName]);
                        createNotification(
                            'Such name is already used.',
                            'error'
                        );
                    } else {
                        createNotification(
                            'An unexpected error occurred while renaming.',
                            'error'
                        );
                    }
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while renaming.',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const renderForm = () => (
        <Form
            initialValues={{
                newName: `${props.currentName}`,
            }}
            validationSchema={yup.object().shape({
                newName: yup
                    .string()
                    .required('Name is required')
                    .notOneOf(existingNames, 'This name is already used'),
            })}
            onSubmit={(values: CopyTemplateFormValues) =>
                rename(values.newName)
            }>
            <div className="d-flex flex-column">
                <div className="form-group row">
                    <div className="col-md-3 d-flex mt-2">
                        <label className="rename-label" htmlFor="newName">
                            New Name:
                        </label>
                    </div>
                    <div className="col-md-9">
                        <Field
                            type="text"
                            name="newName"
                            id="newName"
                            placeholder="Enter group name"
                            className="form-control"
                        />
                    </div>
                </div>
                <div className="mt-auto d-flex justify-content-end modal-rename-buttons">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => props.onClose()}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isLoading}>
                        Rename
                    </button>
                </div>
            </div>
        </Form>
    );

    const renderLoader = () => {
        return (
            <div className="loader-container" style={{ height: '10vw' }}>
                <div className="loader-container-inner">
                    <div className="text-center">
                        <Loader active={isLoading} type="ball-scale-multiple" />
                    </div>
                    <h6 className="mt-5">Renaming...</h6>
                </div>
            </div>
        );
    };

    return (
        <div>
            <Rodal
                visible={props.isVisible}
                animation={'fade'}
                onClose={() => props.onClose()}
                showMask={true}
                width={600}
                closeOnEsc
                className="modal-rename-popup"
                center>
                <ModalHeader>{props.modalTitle || 'Document Template Rename'}</ModalHeader>
                <ModalBody>
                    {isLoading ? renderLoader() : renderForm()}
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default DocumentTemplateRenameModal;
